/**
 * ColumnFilterPopover — popover chứa filter content theo `kind`.
 *
 * Caller cung cấp:
 *   - open / anchorEl / onClose       : trạng thái popover
 *   - label                            : tiêu đề (vd column.label)
 *   - kind                             : 'text' | 'checkboxes' | 'date' | 'number'
 *   - value / onChange                 : controlled binding với useColumnFilters
 *   - options                          : (chỉ khi kind='checkboxes') distinct values
 *
 * 4 nội dung con (TextFilterPanel / CheckboxesFilterPanel / DateFilterPanel /
 * NumberFilterPanel) đều có Clear button trả về `null` để useColumnFilters
 * tự xoá entry.
 */
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItemButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import type { ColumnFilterAnchorPosition } from './ColumnFilterIcon'
import type { ColumnFilterValue } from './columnFilters'

export type ColumnFilterPanelKind = 'text' | 'checkboxes' | 'date' | 'number'

export interface ColumnFilterPopoverProps {
  open: boolean
  /** Viewport coords từ ColumnFilterIcon. Dùng anchorPosition (không anchorEl)
   *  để tránh fallback (0,0) khi TableVirtuoso remount header. */
  anchorPosition: ColumnFilterAnchorPosition | null
  onClose: () => void
  label: string
  kind: ColumnFilterPanelKind
  value: ColumnFilterValue | undefined
  onChange: (next: ColumnFilterValue | null) => void
  /** Chỉ dùng khi kind = 'checkboxes' — caller compute distinctColumnValues(baseRows, key). */
  options?: string[]
}

export function ColumnFilterPopover({
  open,
  anchorPosition,
  onClose,
  label,
  kind,
  value,
  onChange,
  options,
}: ColumnFilterPopoverProps) {
  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? { top: 0, left: 0 }}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 1.5, minWidth: 260, maxWidth: 360 } }}
    >
      {kind === 'text' && <TextFilterPanel label={label} value={value} onChange={onChange} />}
      {kind === 'checkboxes' && (
        <CheckboxesFilterPanel label={label} value={value} onChange={onChange} options={options ?? []} />
      )}
      {kind === 'date' && <DateFilterPanel label={label} value={value} onChange={onChange} />}
      {kind === 'number' && <NumberFilterPanel label={label} value={value} onChange={onChange} />}
    </Popover>
  )
}

interface PanelProps {
  label: string
  value: ColumnFilterValue | undefined
  onChange: (next: ColumnFilterValue | null) => void
}

function TextFilterPanel({ label, value, onChange }: PanelProps) {
  const current = value?.kind === 'text' ? value.value : ''
  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        label={label}
        placeholder="Contains..."
        value={current}
        onChange={(e) => onChange({ kind: 'text', value: e.target.value })}
      />
      <Button size="small" variant="text" onClick={() => onChange(null)}>
        Clear
      </Button>
    </Stack>
  )
}

interface CheckboxesPanelProps extends PanelProps {
  options: string[]
}

function CheckboxesFilterPanel({ label, value, onChange, options }: CheckboxesPanelProps) {
  const selected = value?.kind === 'checkboxes' ? value.selected : []
  const [search, setSearch] = useState('')

  const visible = useMemo(() => {
    if (!search.trim()) return options
    const needle = search.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(needle))
  }, [options, search])

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]
    onChange(next.length > 0 ? { kind: 'checkboxes', selected: next } : null)
  }

  return (
    <Stack spacing={0.5} sx={{ minWidth: 240 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {options.length > 8 && (
        <TextField
          size="small"
          fullWidth
          placeholder="Filter list..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <Box
        sx={{
          maxHeight: 280,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
        }}
      >
        <List dense disablePadding>
          {visible.map((opt) => (
            <ListItemButton
              key={opt}
              dense
              onClick={() => toggle(opt)}
              sx={{ py: 0, px: 0.5 }}
            >
              {/* tabIndex=-1 + disableRipple: tránh focus + double-fire onClick.
                  Toggle handled bởi ListItemButton bao ngoài → click bất kỳ chỗ nào
                  trên row đều tick được. */}
              <Checkbox
                size="small"
                checked={selected.includes(opt)}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0.5, mr: 0.5 }}
              />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {opt || '(blank)'}
              </Typography>
            </ListItemButton>
          ))}
          {visible.length === 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ p: 1, display: 'block' }}>
              No options
            </Typography>
          )}
        </List>
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Button
          size="small"
          variant="text"
          disabled={visible.length === 0}
          onClick={() => onChange({ kind: 'checkboxes', selected: visible })}
        >
          Select shown
        </Button>
        <Button size="small" variant="text" onClick={() => onChange(null)}>
          Clear
        </Button>
      </Stack>
    </Stack>
  )
}

function DateFilterPanel({ label, value, onChange }: PanelProps) {
  const from = value?.kind === 'date' ? value.from : null
  const to = value?.kind === 'date' ? value.to : null

  const setRange = (nextFrom: string | null, nextTo: string | null) => {
    if (nextFrom == null && nextTo == null) onChange(null)
    else onChange({ kind: 'date', from: nextFrom, to: nextTo })
  }

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <DatePicker
        label="From"
        value={from ? dayjs(from) : null}
        onChange={(d) => setRange(d ? d.format('YYYY-MM-DD') : null, to)}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
      <DatePicker
        label="To"
        value={to ? dayjs(to) : null}
        onChange={(d) => setRange(from, d ? d.format('YYYY-MM-DD') : null)}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
      <Button size="small" variant="text" onClick={() => onChange(null)}>
        Clear
      </Button>
    </Stack>
  )
}

function NumberFilterPanel({ label, value, onChange }: PanelProps) {
  const min = value?.kind === 'number' ? value.min : null
  const max = value?.kind === 'number' ? value.max : null

  const setRange = (nextMin: number | null, nextMax: number | null) => {
    if (nextMin == null && nextMax == null) onChange(null)
    else onChange({ kind: 'number', min: nextMin, max: nextMax })
  }

  const parse = (s: string): number | null => {
    const t = s.trim()
    if (t === '') return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Min"
        type="number"
        value={min ?? ''}
        onChange={(e) => setRange(parse(e.target.value), max)}
      />
      <TextField
        size="small"
        fullWidth
        label="Max"
        type="number"
        value={max ?? ''}
        onChange={(e) => setRange(min, parse(e.target.value))}
      />
      <Button size="small" variant="text" onClick={() => onChange(null)}>
        Clear
      </Button>
    </Stack>
  )
}
